import { createLogger } from "logging";
import { LogLevel } from "models/LogLevel";
import { TranslationLabels } from "translations/TranslationLabels";

import { ResourceKey } from "./resourceKeys";

const logger = createLogger("TranslationsProvider");

export function isResourceKey(key: unknown): key is ResourceKey {
  return typeof key === "string" && key in (window.labels ?? {});
}

export function getResource(
  key: string,
  defaultValue: string | null = null,
  logWarning = true,
  labelsOverride: TranslationLabels | undefined = undefined
): string {
  const keyLowerCase = key.toLowerCase();
  const defaultValueToUse = defaultValue === null ? `$Resources:${key}` : defaultValue;

  const labelsToUse = labelsOverride ?? window.labels ?? {};
  const entries = Object.entries(labelsToUse);
  const matches = entries.filter((x) => x[0].toLowerCase() == keyLowerCase);
  if (!matches.length) {
    const logLevel: LogLevel = logWarning ? LogLevel.Warn : LogLevel.Trace;
    logger.log(logLevel, `getResource(): key:='${key}' does not exist!`);
    return defaultValueToUse;
  }

  const value = `${matches[0][1]}`;
  if (!value.length) {
    const logLevel: LogLevel = logWarning ? LogLevel.Warn : LogLevel.Trace;
    logger.log(logLevel, `getResource(): key:='${key}' value is empty!`);
    return defaultValueToUse;
  }

  return value;
}

/**
 * Like `getResource`, but throws a `TypeError` if the key is not found or its value is empty.
 * Use this when the key is statically known or derived from a finite enum — i.e. a missing
 * translation is always a bug and should fail loudly rather than showing a sentinel string.
 */
export function getResourceStrict(key: ResourceKey): string {
  const value = getResource(key);
  if (!value) {
    throw new TypeError(`getResourceStrict(): required key '${key}' not found in translation labels`);
  }
  return value;
}
